/**
 * Trang chi tiết ngành phía sinh viên (Sprint 31) — `/majors/:slug`.
 *
 * Hiện khung chương trình (Term theo `order`) + gợi ý môn học nên đăng ký
 * cho kỳ hiện tại (xem `SuggestedCourses`, suy ra động qua
 * `useSuggestedCourses` — không cần field lưu riêng).
 */

import { Breadcrumb, Result, Space, Spin, Tag, Typography } from "antd";
import { HomeOutlined } from "@ant-design/icons";
import { Link, useParams } from "react-router-dom";
import { useMajorDetail } from "@/api/catalog";
import { useSuggestedCourses } from "@/api/enrollment";
import { SuggestedCourses } from "@/components/SuggestedCourses";
import { MajorTermsCard } from "@/components/MajorTermsCard";

const { Title, Text, Paragraph } = Typography;

export function MajorPublicDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { data: major, isLoading, error } = useMajorDetail(slug);
  // majorId chỉ có sau khi major load xong — hook tự chờ nhờ `enabled: !!majorId`
  // ở tầng useQuery bên trong useSuggestedCourses (React Query, không cần thêm guard).
  const { data: suggestions } = useSuggestedCourses(major?.id);

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: 64 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (error || !major) {
    return (
      <Result
        status="404"
        title="Không tìm thấy ngành học"
        extra={<Link to="/profile">← Về hồ sơ</Link>}
      />
    );
  }

  const currentTermId = suggestions?.current_term?.id ?? null;

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          {
            title: (
              <Link to="/">
                <HomeOutlined />
              </Link>
            ),
          },
          { title: <Link to="/profile">Hồ sơ</Link> },
          { title: major.name },
        ]}
      />

      <Space size="small" wrap style={{ marginBottom: 4 }}>
        <Tag color="blue">{major.code}</Tag>
        <Text type="secondary">{major.program_name}</Text>
      </Space>
      <Title level={2} style={{ margin: "4px 0 8px" }}>
        {major.name}
      </Title>
      {major.description && <Paragraph type="secondary">{major.description}</Paragraph>}

      <SuggestedCourses majorId={major.id} variant="full" />

      <MajorTermsCard terms={major.terms} currentTermId={currentTermId} />
    </div>
  );
}

export default MajorPublicDetail;
